import * as Sentry from '@sentry/browser';

export async function copyHtmlToClipboard(content: string) {
  const blob = new Blob([content], { type: 'text/html' });
  const richTextInput = new ClipboardItem({ 'text/html': blob });
  await navigator.clipboard.write([richTextInput]);
}

export async function copyToClipboard(inputId: string) {
  let copyText = document.getElementById(inputId) as HTMLInputElement;
  copyText.select();
  copyText.setSelectionRange(0, 99999);

  await navigator.clipboard.writeText(copyText.value);
}

export function userCountMatch(metadata: string, userCount: number) {
  const [min, max] = metadata.split('-');
  return +min <= userCount && (!max || userCount <= +max);
}

export const nameof = <T>(name: Extract<keyof T, string>): string => name;

export function getCsrfToken(): string | null {
  return (document.querySelector('meta[name=csrf-token]') as HTMLMetaElement)?.content;
}

export function getCookie(name: string) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
}

export function getCurrentUrl() {
  const url = `${window.location.origin}${window.location.pathname}`;
  return url.endsWith('/') ? url.slice(0, -1) : url;
}

export function getBaseUrl() {
  const url = `${window.location.origin}`;
  return url.endsWith('/') ? url.slice(0, -1) : url;
}

// Fetch only the root domain and TLD (e.g. 'galacticscan.com') and prepend with 'portal.'
//   Very useful when API requests to alternate domains (e.g. 'updatedsecuritytest.com') fail when accessing the 'www.' FQDN.
export function getPortalFqdn() {
  let re = new RegExp(/^([a-z]+:\/\/).*?((?:(?:qa|test|dev)\.)?[^\.]+\.[a-zA-Z0-9]+)(?:$|\/)/);
  let captures = re.exec(window.location.origin);

  // If regex fails (for ex: 'localhost' queries) then don't send back an altered FQDN.
  if (!captures || captures.length < 3) return window.location.origin;

  return `${captures[1]}portal.${captures[2]}`;
}

export function getPortalUrl() {
  const url = `${getPortalFqdn()}${window.location.pathname}`;
  return url.endsWith('/') ? url.slice(0, -1) : url;
}

export function getHashlessCurrentUrl() {
  return window.location.href.replace(window.location.hash, '');
}

export const average = (array: any[]) => array.reduce((a, b) => a + b) / array.length;

export const isTest = () => window.location.origin.includes('.test.') || window.location.origin.includes('localhost');

export const isProduction = () => !isTest();

export const formatPhoneNumber = (number: string) => {
  const cleaned = ('' + number).replace(/\D/g, '');
  const match = cleaned.match(/^(\d*)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    const intlCode = (match[1] || '1') === '1' ? '' : `+${match[1]} `;
    return `${intlCode}(${match[2]}) ${match[3]}-${match[4]}`;
  }

  return null;
};

export const reportTransientError = (e: any) => {
  Sentry.captureException(e);
  alert(e.message || 'An unknown error occurred.');
};

export const capitalizeFirstLetter = (str: string): string => {
  return str[0].toUpperCase() + str.slice(1);
};

export const formatRelativeTime = (date: Date): string => {
  const formatter = new Intl.RelativeTimeFormat(undefined, {
    numeric: 'auto',
  });

  const DIVISIONS = [
    { amount: 60, name: 'seconds' },
    { amount: 60, name: 'minutes' },
    { amount: 24, name: 'hours' },
    { amount: 7, name: 'days' },
    { amount: 4.34524, name: 'weeks' },
    { amount: 12, name: 'months' },
    { amount: Number.POSITIVE_INFINITY, name: 'years' },
  ];

  let duration = (date.getTime() - new Date().getTime()) / 1000;

  for (let i = 0; i <= DIVISIONS.length; i++) {
    const division = DIVISIONS[i];
    if (Math.abs(duration) < division.amount) {
      return formatter.format(Math.round(duration), division.name as Intl.RelativeTimeFormatUnit);
    }
    duration /= division.amount;
  }
};

export const toRelativeDate = (relativeDate: Date, startDate: Date) => {
  const secs = Math.floor((relativeDate.getTime() - startDate.getTime()) / 1000);
  if (secs < 60) return secs + ' sec(s) ago';
  if (secs < 3600) return Math.floor(secs / 60) + ' min(s) ago';
  if (secs < 86400) return Math.floor(secs / 3600) + ' hour(s) ago';
  if (secs < 604800) return Math.floor(secs / 86400) + ' day(s) ago';
  return startDate.toDateString();
};

export const toDuration = (startDate: Date, endDate: Date): string => {
  const secs = Math.floor((endDate.getTime() - startDate.getTime()) / 1000);
  if (secs < 60) return secs + ' sec(s)';
  if (secs < 3600) return Math.floor(secs / 60) + ' min(s)';
  if (secs < 86400) return Math.floor(secs / 3600) + ' hour(s)';
  if (secs < 604800) return Math.floor(secs / 86400) + ' day(s)';
  return startDate.toDateString();
};

export const mapCompanyStatus = (status: number): string => {
  switch (status) {
    case 1:
      return 'Active';
    case 2:
      return '90-Day-Start';
    case 3:
      return '90-Day-End';
    case 4:
      return 'Inactive';
    case 5:
      return 'Archived';
    default:
      return '';
  }
};

export const isValidDate = (dateString: string): boolean => {
  return !isNaN(Date.parse(dateString));
};

export const getDate = (date: string, includeTime: boolean = false): string => {
  let options: Intl.DateTimeFormatOptions = {
    dateStyle: 'medium',
  };
  if (includeTime) {
    options.timeStyle = 'short';
  }
  return date === null ? '' : new Date(date).toLocaleString([], options);
};

export const toDollarAmount = (cents: number) => {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

export const mapNumbers = (n: number, oldFrom: number, oldTo: number, newFrom: number, newTo: number) => {
  return ((n - oldFrom) / (oldTo - oldFrom)) * (newTo - newFrom) + newFrom;
};

export const secondsToTime = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds - hours * 3600) / 60);
  seconds = Math.floor(seconds - hours * 3600 - minutes * 60);

  let hoursText = hours.toString() + ':';
  let minutesText = minutes.toString() + ':';
  let secondsText = seconds.toString();

  if (hours < 10) hoursText = '0' + hoursText;
  if (minutes < 10 && hours > 1) minutesText = '0' + minutesText;
  if (seconds < 10) secondsText = '0' + secondsText;

  if (hours === 0) hoursText = '';

  return hoursText + minutesText + secondsText;
};

const isObject = (object: any): boolean => {
  return object != null && typeof object === 'object';
};

export const dynamicObjectComparison = (obj1: any, obj2: any): boolean => {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    const val1 = obj1[key];
    const val2 = obj2[key];

    const areObjects = isObject(val1) && isObject(val2);
    if ((areObjects && !dynamicObjectComparison(val1, val2)) || (!areObjects && val1 !== val2)) {
      return false;
    }
  }

  return true;
};

export const animateValue = (
  obj: HTMLDivElement,
  valueStart: number,
  valueEnd: number,
  durationMS: number,
  labelPrefix?: string,
  labelSuffix?: string
) => {
  let startTimestamp = null as number;

  const step = (timestamp: number) => {
    if (!startTimestamp) startTimestamp = timestamp;
    const progress = Math.min((timestamp - startTimestamp) / durationMS, 1);

    const value = Math.floor(progress * (valueEnd - valueStart) + valueStart).toString();

    obj.innerText = `${labelPrefix ?? ''}${value}${labelSuffix ?? ''}`;

    if (progress < 1) window.requestAnimationFrame(step);
  };

  window.requestAnimationFrame(step);
};

export const limitTextByWords = (text: string, maxWords: number) => {
  const wholeTextArr = text.trim().split(' ');
  const textArr = text.trim().split(' ', maxWords);

  const ellipsis = wholeTextArr.length > maxWords ? '...' : '';

  return textArr.join(' ') + ellipsis;
};
